<script setup lang="ts">
/**
 * The execution graph's legend, as the portal sets it under its canvas: each node state as its glyph and
 * word, then the three edge states. Decoration: every node and edge carries its own word.
 *
 * `tones` picks the states to explain (the four a finished example shows, by default; the portal lists
 * every state a live run can reach), and the default slot adds a hint at the end of the line.
 */
import { EXECUTION_TONE_WORD, type ExecutionNodeTone } from '../lib/surfaces'

withDefaults(defineProps<{ tones?: readonly ExecutionNodeTone[] }>(), {
    tones: () => ['succeeded', 'running', 'action', 'unvisited'],
})
</script>

<template>
    <p class="zh-exec-legend" aria-hidden="true">
        <span v-for="tone in tones" :key="tone" class="zh-exec-legend__item zh-exec-legend__tone" :data-tone="tone">{{
            EXECUTION_TONE_WORD[tone]
        }}</span>
        <span class="zh-exec-legend__item"><i class="zh-exec-legend__edge" data-state="taken" />Taken</span>
        <span class="zh-exec-legend__item"><i class="zh-exec-legend__edge" data-state="current" />Now</span>
        <span class="zh-exec-legend__item"><i class="zh-exec-legend__edge" data-state="idle" />Not taken</span>
        <span v-if="$slots.default" class="zh-exec-legend__hint"><slot /></span>
    </p>
</template>

<style scoped>
.zh-exec-legend {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem 1.5rem;
    margin: 0;
    padding-block: 0.75rem;
    color: var(--ink-3);
    font-family: var(--font-mono);
    font-size: 0.75rem;
    font-weight: 500;
    letter-spacing: var(--tracking-system);
    text-transform: uppercase;
}

.zh-exec-legend__item {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
}

.zh-exec-legend__tone::before {
    font-size: 0.6875rem;
}

.zh-exec-legend__tone[data-tone="succeeded"]::before {
    content: "✓" / "";
    color: var(--state-complete);
}

.zh-exec-legend__tone[data-tone="running"]::before {
    content: "●" / "";
    color: var(--state-active);
}

.zh-exec-legend__tone[data-tone="action"]::before {
    content: "▲" / "";
    color: var(--state-action);
}

.zh-exec-legend__tone[data-tone="failed"]::before {
    content: "✕" / "";
    color: var(--state-failed);
}

/* The parked states are a small dot, as on the node. */
.zh-exec-legend__tone[data-tone="unvisited"]::before,
.zh-exec-legend__tone[data-tone="paused"]::before,
.zh-exec-legend__tone[data-tone="queued"]::before,
.zh-exec-legend__tone[data-tone="cancelled"]::before {
    content: "";
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: var(--state-inactive);
}

.zh-exec-legend__edge {
    display: inline-block;
    width: 1.5rem;
    height: 0;
    border-top: 2.5px solid var(--state-complete);
}

.zh-exec-legend__edge[data-state="current"] {
    border-top-color: var(--state-active);
}

.zh-exec-legend__edge[data-state="idle"] {
    border-top: 1.5px dashed var(--state-inactive);
}

.zh-exec-legend__hint {
    margin-left: auto;
    text-transform: none;
    letter-spacing: normal;
}
</style>
